import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { trackMetaEvent } from "@/lib/metaPixel";
import type { AnyValue } from "@/lib/rowTypes";

// Module-level in-flight cache. Keyed by `task|stableHash(context)`.
// Entries are removed as soon as the underlying promise settles, so this
// only protects against duplicate concurrent calls (re-renders, scroll
// re-mounts, React strict-mode double-fire). It is NOT a persistent cache.
const inFlightAICalls = new Map<string, Promise<AnyValue>>();

function stableStringify(value: AnyValue): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, AnyValue>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
}

export interface QuizRemediationData {
  title: string;
  explanation: string;
  misconception: string;
  example: string;
  hint: string;
  recommended_next_step: "retry_now" | "review_lesson";
  recommended_review_topic: string;
}

export interface QuizExplanationData {
  feedback_headline: string;
  explanation: string;
  concept_tested: string;
  trap_answer_reason: string;
  example: string;
  vocabulary_term?: string | null;
  encouragement: string;
}

export interface ModuleRemediationData {
  mastery_gap_summary: string;
  likely_problem_areas: string[];
  retry_strategy: string;
  infographic_text: string;
  encouragement: string;
}

export interface ParentSummaryData {
  summary: string;
  strengths: string[];
  weaknesses: string[];
  fci_explanation: string;
  recommended_actions: string[];
  encouragement_message: string;
}

export interface TutorHelpData {
  answer: string;
  analogy: string;
  key_takeaway: string;
}

export interface OnboardingPathData {
  onboarding_summary: string;
  key_points_to_explain: string[];
  recommended_first_focus: string;
  personalized_tip: string;
}

export interface EventGenerationData {
  event_title: string;
  event_text: string;
  decision_options: { label: string; outcome_hint: string; impact_type: "positive" | "negative" | "neutral" }[];
  learning_objective: string;
  category_tag: string;
}

export interface ModuleTutorData {
  guidance: string;
  strategic_hint: string;
  concept_explanation: string;
  caution_message?: string;
}

export interface EventGuidanceData {
  guidance: string;
  strategic_hint: string;
  concept_explanation: string;
}

export interface AdaptiveCoachData {
  what_was_misunderstood: string;
  correct_answer_strength: string;
  concept_contrast: string;
  suggested_next_step: "retry" | "vocab_review" | "company_case_revisit" | "related_lesson" | "module_retry";
  next_step_detail: string;
  concept_tags: string[];
}

export interface CheckpointDebriefData {
  learner_summary: string;
  parent_summary: string;
  admin_insight: string;
  strengths: string[];
  weak_spots: string[];
  skill_breakdown: { skill: string; rating: "strong" | "developing" | "weak"; note: string }[];
  recommendation_path: string;
  confidence_note: string;
}

export interface MasteryGapData {
  repeated_weak_concepts: string[];
  contrast_confusions: { term_a: string; term_b: string; confusion_note: string }[];
  shallow_recall_flags: string[];
  recommendations: { action: string; target: string; reason: string; priority: number }[];
  summary: string;
}

export interface ModuleFeedbackData {
  what_went_well: string;
  weakening_decision: string;
  concept_applied: string;
  hidden_tradeoff: string;
  do_differently: string;
  related_lesson_concepts: string[];
}

type AiTask =
  | "quiz_remediation"
  | "quiz_explanation"
  | "module_remediation"
  | "parent_summary"
  | "tutor_help"
  | "tutor_hint"
  | "onboarding_path"
  | "event_generation"
  | "event_guidance"
  | "module_tutor"
  | "adaptive_coach"
  | "checkpoint_debrief"
  | "mastery_gap_detect"
  | "module_feedback";

interface AiResult<T> {
  data: T | null;
  fallback: boolean;
  sessionId?: string | null;
}

export function useAITutor() {
  const [loading, setLoading] = useState(false);

  const callAI = useCallback(async <T>(
    task: AiTask,
    context: Record<string, AnyValue>
  ): Promise<AiResult<T>> => {
    setLoading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const enrichedContext = {
        ...context,
        userId: context.userId ?? authData.user?.id ?? null,
      };

      // In-flight de-dup: coalesce identical concurrent calls so a re-render
      // or strict-mode double-mount doesn't fire ai-tutor twice. Cache entry
      // is cleared as soon as the promise settles — this is not a long-lived
      // cache, just protection against duplicate concurrent calls.
      const cacheKey = `${task}|${stableStringify(enrichedContext)}`;
      let invokePromise = inFlightAICalls.get(cacheKey);
      const cacheHit = inFlightAICalls.has(cacheKey);
      if (!invokePromise) {
        invokePromise = supabase.functions
          .invoke("ai-tutor", { body: { task, context: enrichedContext } })
          .finally(() => inFlightAICalls.delete(cacheKey));
        inFlightAICalls.set(cacheKey, invokePromise);
      }
      if (cacheHit) {
        void trackMetaEvent("ai_explanation_cached", { customData: { task } });
      }
      const { data: fnData, error: fnError } = await invokePromise;

      if (fnError) {
        console.warn("AI function returned non-2xx:", fnError);
        toast.error("AI help is temporarily unavailable");
        void trackMetaEvent("ai_explanation_failed", { customData: { task, reason: "fn_error" } });
        return { data: null, fallback: true, sessionId: null };
      }

      if (fnData?.error_code === "rate_limited") {
        toast.error("AI is busy — please try again in a moment.");
        return { data: (fnData?.data as T) ?? null, fallback: true, sessionId: fnData?.session_id ?? null };
      }

      if (fnData?.error_code === "usage_limit_reached" || fnData?.error_code === "auth_failed") {
        toast.error(fnData?.error || "AI service configuration issue");
        return { data: (fnData?.data as T) ?? null, fallback: true, sessionId: fnData?.session_id ?? null };
      }

      if (fnData?.fallback || fnData?.error) {
        if (fnData?.error) {
          toast.error(fnData.error);
        }
        console.warn("AI fallback:", fnData?.error || "fallback response");
        void trackMetaEvent("ai_explanation_failed", {
          customData: { task, reason: fnData?.error_code || "fallback" },
        });
        return { data: (fnData?.data as T) ?? null, fallback: true, sessionId: fnData?.session_id ?? null };
      }

      if (!cacheHit) {
        void trackMetaEvent("ai_explanation_generated", { customData: { task } });
      }
      return {
        data: (fnData?.data as T) ?? null,
        fallback: false,
        sessionId: fnData?.session_id ?? null,
      };
    } catch (err) {
      console.error("AI call failed:", err);
      void trackMetaEvent("ai_explanation_failed", { customData: { task, reason: "exception" } });
      return { data: null, fallback: true, sessionId: null };
    } finally {
      setLoading(false);
    }
  }, []);

  const getQuizRemediation = useCallback(
    (context: {
      lessonName: string;
      courseName: string;
      lessonId?: string;
      courseId?: string;
      score: number;
      totalQuestions: number;
      wrongAnswers?: { question: string; selectedAnswer: string; correctAnswer: string }[];
      ageBracket?: string;
      weakTopics?: string[];
      readingLevel?: string;
      currentFci?: number;
      retryCount?: number;
    }) => callAI<QuizRemediationData>("quiz_remediation", context),
    [callAI]
  );

  const getQuizExplanation = useCallback(
    (context: {
      lessonId: string;
      lessonName: string;
      courseName: string;
      questionId?: string;
      questionText: string;
      answerOptions: { key: string; text: string }[];
      selectedAnswer: string;
      correctAnswer: string;
      selectedAnswerText?: string;
      correctAnswerText?: string;
      wasCorrect: boolean;
      staticExplanation?: string | null;
      ageBracket?: string;
      responseTimeMs?: number;
      hintUsageCount?: number;
      responseMode?: "short" | "detailed";
    }) => callAI<QuizExplanationData>("quiz_explanation", context),
    [callAI]
  );

  const getModuleRemediation = useCallback(
    (context: {
      moduleKey: string;
      moduleName: string;
      score: number;
      maxScore: number;
      ageBracket?: string;
      wrongQuestions?: { question: string; correctAnswer: string }[];
      weakTopics?: string[];
      retryCount?: number;
      currentFci?: number;
    }) => callAI<ModuleRemediationData>("module_remediation", context),
    [callAI]
  );

  const getParentSummary = useCallback(
    (context: {
      childName: string;
      childUserId?: string;
      householdId?: string;
      lessonsCompleted: number;
      totalLessons: number;
      coursesCompleted: number;
      totalCourses: number;
      fciScore: number;
      fciTrend: string;
      strongTopics: string[];
      weakTopics: string[];
      moduleScores: { module_key: string; score: number; max_score: number; passed: boolean }[];
      streakDays: number;
    }) => callAI<ParentSummaryData>("parent_summary", context),
    [callAI]
  );

  const getTutorHelp = useCallback(
    (context: {
      question: string;
      lessonId?: string;
      lessonName: string;
      courseName: string;
      courseId?: string;
      currentQuestion?: string;
      ageBracket?: string;
      readingLevel?: string;
      responseMode?: "short" | "detailed";
    }) => callAI<TutorHelpData>("tutor_help", context),
    [callAI]
  );

  const getTutorHint = useCallback(
    (context: {
      question: string;
      lessonId?: string;
      lessonName: string;
      courseName: string;
      courseId?: string;
      currentQuestion?: string;
      ageBracket?: string;
      responseMode?: "short" | "detailed";
    }) => callAI<TutorHelpData>("tutor_hint", context),
    [callAI]
  );

  const getOnboardingPath = useCallback(
    (context: {
      role: string;
      childAgeBracket?: string;
      startingFci?: number;
      householdSetupState?: string;
      priorExperience?: string;
    }) => callAI<OnboardingPathData>("onboarding_path", context),
    [callAI]
  );

  const generateEvent = useCallback(
    (context: {
      ageBracket?: string;
      currentCourseBand?: string;
      recentEventHistory?: string[];
      targetEventType?: string;
      difficultyLevel?: string;
    }) => callAI<EventGenerationData>("event_generation", context),
    [callAI]
  );

  const getModuleTutorHelp = useCallback(
    (context: {
      moduleId: string;
      moduleContext?: string;
      learnerQuestion: string;
      currentState?: Record<string, AnyValue>;
      ageBracket?: string;
      weakTopics?: string[];
      responseMode?: "short" | "detailed";
    }) => callAI<ModuleTutorData>("module_tutor", context),
    [callAI]
  );

  const getEventGuidance = useCallback(
    (context: {
      eventTitle: string;
      eventText: string;
      options?: { label: string; outcome_hint?: string }[];
      ageBracket?: string;
    }) => callAI<EventGuidanceData>("event_guidance", context),
    [callAI]
  );

  const getAdaptiveCoaching = useCallback(
    (context: {
      lessonName: string;
      courseName: string;
      questionText: string;
      selectedAnswer: string;
      correctAnswer: string;
      selectedAnswerText?: string;
      correctAnswerText?: string;
      ageBracket?: string;
      masteryHistory?: Record<string, AnyValue>[];
      priorWeakSpots?: string[];
      conceptTags?: string[];
    }) => callAI<AdaptiveCoachData>("adaptive_coach", context),
    [callAI]
  );

  const getCheckpointDebrief = useCallback(
    (context: {
      assessmentTitle: string;
      assessmentType: string;
      score: number;
      maxScore: number;
      passed: boolean;
      skillBreakdown?: Record<string, AnyValue>;
      strengths?: string[];
      weaknesses?: string[];
      ageBracket?: string;
    }) => callAI<CheckpointDebriefData>("checkpoint_debrief", context),
    [callAI]
  );

  const detectMasteryGaps = useCallback(
    (context: {
      masteryScores?: Record<string, AnyValue>[];
      misconceptions?: Record<string, AnyValue>[];
      vocabFlags?: string[];
      quizMisses?: Record<string, AnyValue>[];
      moduleOutcomes?: Record<string, AnyValue>[];
      ageBracket?: string;
    }) => callAI<MasteryGapData>("mastery_gap_detect", context),
    [callAI]
  );

  const getModuleFeedback = useCallback(
    (context: {
      moduleName: string;
      moduleKey: string;
      finalMetrics?: Record<string, AnyValue>;
      keyDecisions?: Record<string, AnyValue>[];
      outcome?: string;
      ageBracket?: string;
    }) => callAI<ModuleFeedbackData>("module_feedback", context),
    [callAI]
  );

  return {
    loading,
    getQuizRemediation,
    getQuizExplanation,
    getModuleRemediation,
    getParentSummary,
    getTutorHelp,
    getTutorHint,
    getOnboardingPath,
    generateEvent,
    getEventGuidance,
    getModuleTutorHelp,
    getAdaptiveCoaching,
    getCheckpointDebrief,
    detectMasteryGaps,
    getModuleFeedback,
  };
}
